import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { Flex, RemoteImage, Typography } from '@banhala/ui-kit';
import type { OutfitCarouselProps, OutfitCarouselEntity } from './types';
import { bridge, localizeNumber } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const OutfitCarousel = ({
  entity,
  logging,
}: OutfitCarouselProps) => {
  if (!entity || entity.length === 0) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: 16,
        gap: 8,
      }}
    >
      {entity.map((item, index) => (
        <OutfitCard
          key={`${item.item.goods_deeplink}-${index}`}
          entity={item}
          logging={logging}
        />
      ))}
    </ScrollView>
  );
};

const OutfitCard = ({
  entity,
  logging,
}: {
  entity: OutfitCarouselEntity;
  logging: IScreensLogging;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();

  const handlePressOutfit = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_CONTENT',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(item.outfit_deeplink);
  }, [item.outfit_deeplink, logging, entity.logging, openDeeplink]);

  const handlePressGoods = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_GOODS',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(item.goods_deeplink);
  }, [item.goods_deeplink, logging, entity.logging, openDeeplink]);

  return (
    <Flex width={160}>
      {/* 코디 이미지 */}
      <Flex
        width={160}
        height={200}
        borderRadius={4}
        overflow="hidden"
        onPress={handlePressOutfit}
      >
        <RemoteImage
          uri={item.image}
          width={160}
          height={200}
          resizeMode="cover"
        />
        {item.tag.length > 0 && (
          <Flex
            position="absolute"
            top={8}
            left={8}
            backgroundColor="bg_always_white"
            borderRadius={2}
            padding={{ horizontal: 4, vertical: 1 }}
          >
            <Typography type="subtitle6" color="content_primary">
              {item.tag}
            </Typography>
          </Flex>
        )}
      </Flex>

      {/* 상품 정보 */}
      <Flex.Row
        margin={{ top: 8 }}
        gap={8}
        alignItems="center"
        onPress={handlePressGoods}
      >
        <Flex
          width={36}
          height={36}
          borderRadius={4}
          overflow="hidden"
        >
          <RemoteImage
            uri={item.goods_image}
            width={36}
            height={36}
            resizeMode="cover"
          />
        </Flex>
        <Flex flex={1} gap={2}>
          <Typography type="body3" color="content_primary" numberOfLines={1}>
            {item.goods_name}
          </Typography>
          <Flex.Row alignItems="center" gap={4}>
            {item.discount_rate != null && item.discount_rate > 0 && (
              <Typography type="subtitle3" color="content_pink_primary">
                {item.discount_rate}%
              </Typography>
            )}
            <Typography type="subtitle3" color="content_primary">
              {localizeNumber(item.price)}원
            </Typography>
          </Flex.Row>
        </Flex>
      </Flex.Row>
    </Flex>
  );
};
